import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import LoginPage from "@/pages/LoginPage";
import DistributorDashboard from "@/pages/DistributorDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import DispatcherDashboard from "@/pages/DispatcherDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import BOLManagement from "@/pages/BOLManagement";
import PaymentsPage from "@/pages/PaymentsPage";
import SettingsPage from "@/pages/SettingsPage";
import ChatPage from "@/pages/ChatPage";
import AdminChatControlPage from "@/pages/AdminChatControlPage";
import { AboutPage, CareersPage, EmergencyServicesPage, InvestorsPage, NavigationPage, PrivacyPage, SiteMapPage, TermsPage } from "@/pages/PublicInfoPages";
import MarketplacePage from "@/pages/MarketplacePage";
import SalesRepDashboard from "@/pages/SalesRepDashboard";
import WalletPage from "@/pages/WalletPage";
import DriverTrackingAgent from "@/components/DriverTrackingAgent";
import DriverNavigationConsole from "@/components/DriverNavigationConsole";
import EmergencyDashboard from "@/pages/EmergencyDashboard";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={`/${user.role}`} replace />;
  }
  
  return children;
};

const RoleRedirect = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  switch (user.role) {
    case 'distributor':
      return <Navigate to="/distributor" replace />;
    case 'driver':
      return <Navigate to="/driver" replace />;
    case 'dispatcher':
      return <Navigate to="/dispatcher" replace />;
    case 'admin':
      return <Navigate to="/admin" replace />;
    case 'sales_rep':
      return <Navigate to="/sales-rep" replace />;
    case 'emergency':
      return <Navigate to="/emergency" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/investors" element={<InvestorsPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/navigation" element={<NavigationPage />} />
            <Route path="/emergency-services" element={<EmergencyServicesPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/sitemap" element={<SiteMapPage />} />
            <Route path="/" element={<RoleRedirect />} />
            
            <Route
              path="/distributor/*"
              element={
                <ProtectedRoute allowedRoles={['distributor']}>
                  <DistributorDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/driver/*"
              element={
                <ProtectedRoute allowedRoles={['driver']}>
                  <DriverDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/dispatcher/*"
              element={
                <ProtectedRoute allowedRoles={['dispatcher']}>
                  <DispatcherDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/*"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sales-rep/*"
              element={
                <ProtectedRoute allowedRoles={['sales_rep']}>
                  <SalesRepDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/emergency/*"
              element={
                <ProtectedRoute allowedRoles={['emergency']}>
                  <EmergencyDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/bol/*"
              element={
                <ProtectedRoute allowedRoles={['distributor', 'dispatcher', 'driver', 'admin']}>
                  <BOLManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/payments/*"
              element={
                <ProtectedRoute allowedRoles={['distributor', 'dispatcher', 'driver', 'admin']}>
                  <PaymentsPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={['distributor', 'dispatcher', 'driver', 'admin', 'sales_rep', 'emergency']}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chat"
              element={
                <ProtectedRoute allowedRoles={['distributor', 'dispatcher', 'driver', 'admin', 'sales_rep', 'emergency']}>
                  <ChatPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/wallet"
              element={
                <ProtectedRoute allowedRoles={['driver', 'sales_rep']}>
                  <WalletPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/marketplace"
              element={
                <ProtectedRoute allowedRoles={['distributor', 'dispatcher', 'driver', 'admin']}>
                  <MarketplacePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/chat-control"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminChatControlPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          <DriverTrackingAgent />
          <DriverNavigationConsole />
          <InstallAppPrompt />
          <Toaster richColors position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
